import Phaser from 'phaser';
import { BaseScene } from '@core/scenes/BaseScene';
import { Palette, SceneKeys } from '@core/config/constants';
import { SHOP_ITEMS, type ShopItem } from '@core/config/shop';

const MAX_CARD_WIDTH = 220;

/**
 * Tienda de accesorios: el jugador gasta las estrellas ganadas en los minijuegos para
 * comprar accesorios y equiparlos sobre su personaje. Tocar una tarjeta compra (si no
 * la tiene) o alterna equipar/quitar (si ya es suya).
 */
export class StoreScene extends BaseScene {
  public constructor() {
    super(SceneKeys.Store);
  }

  public create(): void {
    this.initResponsive();
    this.addSelectedBackground();
    this.addTwinkles(18);

    this.addBackButton();
    this.renderCoins();

    this.add
      .text(this.centerX, this.scale.height * 0.07, '🛍️ Tienda', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '46px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);

    this.add
      .text(this.centerX, this.scale.height * 0.07 + 42, 'Gastá tus estrellas en accesorios', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '22px',
        color: '#4dd0e1',
      })
      .setOrigin(0.5);

    this.renderGrid(this.scale.height * 0.2);
  }

  private renderCoins(): void {
    this.add
      .text(this.scale.width - 28, 28, `⭐ ${this.profile.coins}`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '30px',
        color: Palette.textLight,
      })
      .setOrigin(1, 0);
  }

  private renderGrid(gridTop: number): void {
    const { width, height } = this.scale;
    const gridBottom = height - 56;
    const gridAreaHeight = Math.max(160, gridBottom - gridTop);

    const gap = Math.max(16, width * 0.02);
    const idealColumnWidth = 200;
    let columns = Math.max(1, Math.floor((width * 0.92 + gap) / (idealColumnWidth + gap)));
    columns = Math.min(columns, SHOP_ITEMS.length, 4);
    if (this.isPortrait) {
      columns = Math.min(columns, 2);
    }
    const rows = Math.ceil(SHOP_ITEMS.length / columns);

    const cardWidth = Math.min(MAX_CARD_WIDTH, (width * 0.92 - (columns - 1) * gap) / columns);
    const cardHeight = Math.min(cardWidth * 1.05, (gridAreaHeight - (rows - 1) * gap) / rows);

    const totalHeight = rows * cardHeight + (rows - 1) * gap;
    const startY = gridTop + (gridAreaHeight - totalHeight) / 2 + cardHeight / 2;

    SHOP_ITEMS.forEach((item, index) => {
      const column = index % columns;
      const row = Math.floor(index / columns);
      const cardsInRow = Math.min(columns, SHOP_ITEMS.length - row * columns);
      const rowWidth = cardsInRow * cardWidth + (cardsInRow - 1) * gap;
      const startX = this.centerX - rowWidth / 2 + cardWidth / 2;

      const x = startX + column * (cardWidth + gap);
      const y = startY + row * (cardHeight + gap);
      this.createItemCard(item, x, y, cardWidth, cardHeight, index * 60);
    });
  }

  private createItemCard(
    item: ShopItem,
    x: number,
    y: number,
    cardWidth: number,
    cardHeight: number,
    entranceDelay: number,
  ): void {
    const owned = this.profile.ownsItem(item.id);
    const equipped = this.profile.accessory === item.id;

    const container = this.add.container(x, y);

    const background = this.add.graphics();
    background.fillStyle(item.color, owned ? 1 : 0.85);
    background.fillRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 20);
    background.lineStyle(5, Palette.highlight, equipped ? 1 : 0);
    background.strokeRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 20);

    const emoji = this.add
      .text(0, -cardHeight * 0.2, item.emoji, { fontSize: `${Math.round(cardHeight * 0.32)}px` })
      .setOrigin(0.5);

    const name = this.add
      .text(0, cardHeight * 0.08, item.name, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '22px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);

    const status = this.add
      .text(0, cardHeight * 0.32, this.statusLabel(item, owned, equipped), {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '20px',
        color: equipped ? '#1a1033' : Palette.textLight,
        backgroundColor: equipped ? '#ffd54f' : 'rgba(0,0,0,0.25)',
        padding: { x: 12, y: 6 },
      })
      .setOrigin(0.5);

    container.add([background, emoji, name, status]);
    container.setSize(cardWidth, cardHeight);
    container.setInteractive({ useHandCursor: true });

    container.setScale(0.7).setAlpha(0);
    this.tweens.add({
      targets: container,
      scale: 1,
      alpha: 1,
      duration: 280,
      delay: entranceDelay,
      ease: 'Back.easeOut',
    });

    container.on(Phaser.Input.Events.POINTER_OVER, () => container.setScale(1.05));
    container.on(Phaser.Input.Events.POINTER_OUT, () => container.setScale(1));
    container.on(Phaser.Input.Events.POINTER_UP, () => this.onCardTap(item));
  }

  private statusLabel(item: ShopItem, owned: boolean, equipped: boolean): string {
    if (equipped) {
      return 'Equipado ✓';
    }
    if (owned) {
      return 'Equipar';
    }
    return `⭐ ${item.price}`;
  }

  private onCardTap(item: ShopItem): void {
    const owned = this.profile.ownsItem(item.id);

    if (owned) {
      const equipped = this.profile.accessory === item.id;
      this.profile.setAccessory(equipped ? '' : item.id);
      this.audio.playClick();
      this.scene.restart();
      return;
    }

    if (this.profile.buyItem(item.id, item.price)) {
      this.profile.setAccessory(item.id);
      this.audio.playReward();
      this.scene.restart();
      return;
    }

    this.audio.playWrong();
    this.showToast(`Te faltan estrellas para ${item.name}`);
  }

  private showToast(message: string): void {
    const toast = this.add
      .text(this.centerX, this.scale.height - 36, message, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '22px',
        color: '#ffffff',
        backgroundColor: 'rgba(255,45,142,0.9)',
        padding: { x: 16, y: 8 },
      })
      .setOrigin(0.5);

    this.tweens.add({
      targets: toast,
      alpha: 0,
      y: toast.y - 30,
      delay: 900,
      duration: 600,
      onComplete: () => toast.destroy(),
    });
  }
}
